/**
 * Typed errors the {@link ./worker-pool.js WorkerPool} rejects with. Drivers
 * (e.g. cpu-offload) catch these to translate them into their own domain errors.
 *
 * @module worker-pool/errors
 */

/** A unit of work ran longer than its timeout; the child was SIGKILLed. */
export class WorkerPoolTimeoutError extends Error {
  readonly _tag = 'WorkerPoolTimeoutError';
  constructor(timeoutMs: number) {
    super(`worker-pool call exceeded ${timeoutMs}ms and was terminated`);
    this.name = 'WorkerPoolTimeoutError';
  }
}

/**
 * A child exceeded its RSS cap; the supervisor SIGKILLed it — only the offending
 * child, not the whole worker. `rssBytes`/`limitBytes` are public so consumers
 * (logs, telemetry) can read them without parsing the message.
 */
export class WorkerPoolOomError extends Error {
  readonly _tag = 'WorkerPoolOomError';
  constructor(
    readonly rssBytes: number,
    readonly limitBytes: number
  ) {
    super(`worker exceeded RSS cap (${rssBytes} > ${limitBytes} bytes) and was terminated`);
    this.name = 'WorkerPoolOomError';
  }
}

/** The child crashed, the work threw, or the pool could not spawn a child. */
export class WorkerPoolError extends Error {
  readonly _tag = 'WorkerPoolError';
  constructor(message: string) {
    super(message);
    this.name = 'WorkerPoolError';
  }
}

/** A session lease stopped heartbeating and passed its hard TTL backstop. */
export class WorkerPoolLeaseExpiredError extends WorkerPoolError {
  constructor(
    readonly details: {
      createdAt: number;
      lastHeartbeatAt: number;
      expiresAt: number;
      observedAt: number;
      ttlMs: number;
      heartbeatCount: number;
      telemetryContext?: Readonly<Record<string, string>>;
    }
  ) {
    super('lease expired (no heartbeat within ttl)');
    this.name = 'WorkerPoolLeaseExpiredError';
  }
}
